import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

// ============================================
// Layout
// ============================================
export const Wrapper = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const TopBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const TopBarTitle = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
});

export const CardGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
  },
});

// ============================================
// Card
// ============================================
export const Card = styled(Box)({
  backgroundColor: '#F3F9FA',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const CardTitleGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const CardTitle = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
});

export const CardActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SmallButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CopyButton = styled('button')({
  height: '24px',
  width: '48px',
  border: '1px solid #404040',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: '#404040',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
  '&:hover': { backgroundColor: 'rgba(64, 64, 64, 0.06)' },
  '&:disabled': { borderColor: '#D9D9D9', color: '#A6A6A6', cursor: 'not-allowed' },
});

export const DeleteButton = styled('button')({
  height: '24px',
  width: '48px',
  border: '1px solid #EE735A',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: '#EE735A',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
  '&:hover': { backgroundColor: 'rgba(238, 115, 90, 0.08)' },
});

export const CollapseToggle = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  border: 'none',
  background: 'transparent',
  color: '#737373',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
});

// ============================================
// Card Body
// ============================================
export const CardBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const FieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
});

export const FieldLabel = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  width: '32px',
  flexShrink: 0,
  margin: 0,
});

// ============================================
// Day Buttons
// ============================================
export const DayButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  flexWrap: 'nowrap',
});

// flex shrink 허용 + minWidth 0 으로 카드가 좁아져도 밀려나오지 않게 함
const dayBaseStyle = {
  flex: '1 1 0',
  minWidth: 0,
  height: '40px',
  borderRadius: '12px',
  border: 'none',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '0 4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.15s ease',
};

export const DayButton = styled('button', {
  shouldForwardProp: prop => !['isSelected', 'dayKey'].includes(prop),
})(({ isSelected, dayKey }) => {
  if (isSelected) {
    return {
      ...dayBaseStyle,
      backgroundColor: '#4AB3BC',
      color: '#fff',
    };
  }
  // 토요일/일요일 unselected 톤 차별화
  if (dayKey === 'saturday') {
    return {
      ...dayBaseStyle,
      backgroundColor: 'rgba(53, 130, 192, 0.12)',
      color: '#3582C0',
    };
  }
  if (dayKey === 'sunday') {
    return {
      ...dayBaseStyle,
      backgroundColor: 'rgba(238, 115, 90, 0.16)',
      color: '#EE735A',
    };
  }
  return {
    ...dayBaseStyle,
    backgroundColor: '#D9D9D9',
    color: '#262626',
  };
});

// ============================================
// Time Row (ON/OFF)
// ============================================
export const TimeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
});

export const TimeBlock = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
});

export const TimePicker = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  position: 'relative',
});

export const TimeInputWrapper = styled(Box)({
  position: 'relative',
  flex: '1 0 0',
  minWidth: 0,
});

export const TimeInput = styled('button')({
  width: '100%',
  height: '48px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid #D9D9D9',
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
});

export const TimeColon = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
  width: '12px',
  textAlign: 'center',
});

// ============================================
// Time Slot (한 카드 안 여러 개 가능)
// ============================================
export const TimeSlotList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const TimeSlotBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const TimeSlotHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const TimeSlotLabel = styled('p')({
  fontSize: '13px',
  fontWeight: 600,
  color: '#404040',
  margin: 0,
});

export const TimeSlotDeleteButton = styled('button')({
  background: 'transparent',
  border: 'none',
  color: '#EE735A',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
});

export const AddTimeSlotButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  padding: '10px',
  border: '1px dashed #BFBFBF',
  borderRadius: '8px',
  background: 'transparent',
  color: '#737373',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
});

// ============================================
// ON/OFF Tag (label 좌측)
// ============================================
export const OnOffLabel = styled('p', {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  fontSize: '16px',
  fontWeight: variant === 'on' ? 600 : 500,
  color: '#262626',
  margin: 0,
  width: '32px',
  flexShrink: 0,
  textAlign: 'left',
}));

// OFF 가 ON 보다 같거나 빠를 때(자정 넘김) OFF 옆에 표시하는 "익일" 배지
export const NextDayBadge = styled('span')({
  flexShrink: 0,
  padding: '1px 6px',
  borderRadius: '4px',
  background: '#FFF3E0',
  color: '#E8833A',
  fontSize: '11px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

// ============================================
// Time Dropdown
// ============================================
export const TimeDropdown = styled(Box)({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 10,
});

export const TimeDropdownItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  padding: '8px 12px',
  fontSize: '14px',
  fontWeight: isSelected ? 600 : 500,
  color: isSelected ? '#0F7F85' : '#262626',
  backgroundColor: isSelected ? 'rgba(15, 127, 133, 0.08)' : 'transparent',
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'rgba(15, 127, 133, 0.06)' },
}));

// ============================================
// Monitor (Light) Section
// ============================================
export const MonitorSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

export const MonitorLabel = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
});

export const MonitorBox = styled(Box)({
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const MonitorGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  rowGap: '12px',
  columnGap: '8px',
});

export const MonitorItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const MonitorItemLabel = styled('p')({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
  whiteSpace: 'nowrap',
});

// 커스텀 체크박스 아이콘 (Figma 디자인 — 24x24, radius 2, 체크 시 #4AB3BC 배경 + 흰 체크)
const checkboxIconBase = {
  width: '24px',
  height: '24px',
  borderRadius: '2px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

export const CheckboxIconUnchecked = styled('span')({
  ...checkboxIconBase,
  border: '1px solid #D9D9D9',
  backgroundColor: '#fff',
});

export const CheckboxIconChecked = styled('span')({
  ...checkboxIconBase,
  backgroundColor: '#4AB3BC',
  color: '#fff',
});

// ============================================
// Footer (추가하기 + 전체 저장)
// ============================================
export const Footer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const AddScheduleButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'transparent',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  padding: 0,
  color: '#A6A6A6',
  fontSize: '16px',
  fontWeight: 500,
  '&:hover': { color: '#737373' },
});

export const SaveButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  width: '200px',
  padding: '12px 24px',
  borderRadius: '8px',
  backgroundColor: '#0F7F85',
  color: '#fff',
  border: 'none',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#0d6a6f' },
  '&:disabled': { backgroundColor: '#A6A6A6', cursor: 'not-allowed' },
});

export const EmptyMessage = styled(Box)({
  textAlign: 'center',
  padding: '24px',
  color: '#A6A6A6',
  fontSize: '14px',
});
